import React, { memo, useEffect, useLayoutEffect, useState } from "react";
import propTypes from "prop-types";
import classnames from "classnames";
import SelectNonForm from "../nonForm/SelectNonForm";
import "./Pagination.scss";
import "../form-inputs/Dropdown.scss";
import { createDataAttributes } from "../../utilities";
import Label from "../form-inputs/Label";

const calculatePageSettings = (pages, activePage) => {
  // set the start number of the page links
  // ... and how many to show (5 if were's close to the start/end, else show 3)

  // 5 or fewer pages, start on page 1
  if (pages <= 5) {
    return { start: 1, quantity: pages };
  }
  // active page is close to the beginning
  if (activePage < 5) {
    return { start: 1, quantity: 5 };
  }
  // activePage is close to the end
  if (pages - activePage < 4) {
    return { start: pages - 4, quantity: 5 };
  }
  // active page is somewhere in the middle
  if (activePage >= 5) {
    return { start: activePage - 1, quantity: 3 };
  }

  throw new Error("Unable to calculate page settings from values.");
};

const PageLinks = ({ activePage, pages, onPageClick }) => {
  const { start, quantity } = calculatePageSettings(pages, activePage);
  // create array of size quantity and fill it with page numbers based on start value
  const pageNumbers = new Array(quantity)
    // We need entries in array to have a value in order to map...
    .fill("")
    .map((_, i) => i + start);

  return pageNumbers.map(page => (
    <Page
      key={page}
      activePage={activePage}
      page={page}
      className={classnames("uikit__paging__link", {
        uikit__paging__active: page === activePage
      })}
      onClick={onPageClick}
    />
  ));
};

const Page = ({ activePage, page, className, onClick }) => (
  <li
    title={"Go to Page " + page}
    className={classnames("uikit__paging__link", className)}
  >
    <button
      disabled={activePage === page}
      type="button"
      onClick={() => {
        onClick(page);
      }}
    >
      <span className="uikit-sr-only">page</span>
      {page}
    </button>
  </li>
);

const PrevPage = ({ activePage, onClick }) => (
  <li
    className={classnames("uikit__paging__jump uikit__paging__previous", {
      uikit__paging__disabled: activePage === 1
    })}
    title="Previous Page"
  >
    <button type="button" disabled={activePage === 1} onClick={onClick}>
      <span className="uikit-sr-only">Previous Page</span>
    </button>
  </li>
);

const NextPage = ({ activePage, pages, onClick }) => (
  <li
    className={classnames("uikit__paging__jump uikit__paging__next", {
      uikit__paging__disabled: activePage === pages
    })}
    title="Next Page"
  >
    <button type="button" disabled={activePage === pages} onClick={onClick}>
      <span className="uikit-sr-only">Next Page</span>
    </button>
  </li>
);

const Ellipsis = memo(() => (
  <li className="uikit__paging__ellipsis">
    <button disabled>...</button>
  </li>
));

const Pagination = ({
  id,
  activePage,
  goToPage,
  nextPage,
  prevPage,
  pages,
  pageSize,
  defaultPageSize,
  changePageSize,
  totalRows,
  disablePageSizeChange,
  keepInView,
  dataAttrib
}) => {
  const [initialized, setInitialized] = useState(false);
  // create array of page size options that has unique values and is sorted
  const pageSizeOptions = [...new Set([defaultPageSize, 10, 20, 50, 100])].sort(
    (a, b) => a - b
  );

  const paginationRef = React.createRef();

  useEffect(() => {
    setInitialized(true);
  }, []);

  useLayoutEffect(() => {
    if (initialized && activePage && keepInView) {
      // pagination position

      const p = paginationRef.current.getBoundingClientRect();
      // table position
      const t = paginationRef.current.previousSibling.getBoundingClientRect();

      // determine if the table is in a modal
      // if in a, the modal has to scroll instead of the window
      const modalContainer = [
        ...document.querySelectorAll(".uikit__modal__container")
      ].find(m => m.contains(paginationRef.current));

      const offset = modalContainer
        ? modalContainer.scrollTop
        : window.pageYOffset;

      // if it's in a modal, use scrollTop
      if (modalContainer) {
        if (p.top < 0) modalContainer.scrollTop = offset + t.top - 20;

        if (p.top > window.innerHeight)
          // scroll the window up until the pagination is in view
          modalContainer.scrollTop =
            offset + p.bottom - window.innerHeight + 20;
      } else {
        // otherwise, use scroll
        if (p.top < 0) window.scroll(0, offset + t.top - 20);

        if (p.top > window.innerHeight)
          // scroll the window up until the pagination is in view
          window.scroll(0, offset + p.bottom - window.innerHeight + 20);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, pageSize]);

  Ellipsis.displayName = "Ellipsis";

  const handleChangePageSize = ps => {
    if (changePageSize) changePageSize(ps.value);
  };

  // values for the Showing 1 to 10 of 100
  const startOfPage = (activePage - 1) * pageSize + 1;

  const endOfPage =
    totalRows < pageSize
      ? totalRows // if all rows fit on the first page
      : activePage < pages // if we have multiple pages
      ? startOfPage + pageSize - 1 // but not on the last page
      : totalRows; // last page ends on the last row

  return (
    <div
      className="uikit__paging"
      ref={paginationRef}
      {...createDataAttributes(dataAttrib)}
    >
      {totalRows > defaultPageSize && (
        <div className="uikit__paging__showing">
          <div className="uikit__paging__showing__inner">
            Showing {startOfPage} - {endOfPage} of {totalRows} rows
          </div>
        </div>
      )}
      <div className="uikit__paging__page-count">
        {totalRows > defaultPageSize &&
          changePageSize &&
          totalRows > pageSizeOptions[0] &&
          !disablePageSizeChange && (
            <div className="uikit__paging__page-count__inner">
              <SelectNonForm
                className="uikit__paging__page-count__dropdown"
                id={`changepagesize-${id || "no-id"}`}
                name="paging-dropdown"
                title={pageSize}
                value={pageSize}
                onChange={handleChangePageSize}
                options={pageSizeOptions}
                isClearable={false}
                compactIndicator
                noLabel
                size="auto"
              />
              <Label
                htmlFor={`changepagesize-${id || "no-id"}`}
                label="per page"
                labelPosition="after"
                className="uikit__paging__page-count__dropdown__pagination"
              />
            </div>
          )}
      </div>
      {!(changePageSize || totalRows < pageSizeOptions[0]) && (
        <div className="uikit__paging__spacer"></div>
      )}
      {totalRows > pageSize && (
        <div className="uikit__paging__nav">
          <ul className="uikit__paging__links">
            <PrevPage activePage={activePage} onClick={prevPage} />

            {
              // show the first cell/ellipsis if there's more than 5 pages
              // ... and the active page is higher than 4
              pages > 5 && activePage >= 5 && (
                <>
                  <Page activePage={activePage} page={1} onClick={goToPage} />
                  <Ellipsis />
                </>
              )
            }

            <PageLinks
              activePage={activePage}
              pages={pages}
              onPageClick={goToPage}
            />

            {
              // show the last cell/ellipsis if there's more than 5 pages
              // ... and the active page is lower than 5
              // ... or if we're not close to the end
              pages > 5 && (activePage < 5 || pages - activePage >= 4) && (
                <>
                  <Ellipsis />
                  <Page
                    activePage={activePage}
                    page={pages}
                    onClick={goToPage}
                  />
                </>
              )
            }

            <NextPage
              activePage={activePage}
              pages={pages}
              onClick={nextPage}
            />
          </ul>
        </div>
      )}
    </div>
  );
};

Pagination.propTypes = {
  id: propTypes.string,
  activePage: propTypes.number,
  goToPage: propTypes.func,
  nextPage: propTypes.func,
  prevPage: propTypes.func,
  pages: propTypes.number,
  pageSize: propTypes.number,
  changePageSize: propTypes.func,
  defaultPageSize: propTypes.number,
  totalRows: propTypes.number,
  disablePageSizeChange: propTypes.bool,
  keepInView: propTypes.bool,
  dataAttrib: propTypes.arrayOf(Object)
};

Pagination.defaultProps = {
  activePage: 1,
  disablePageSizeChange: false,
  keepInView: false
};

export default Pagination;
